import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../../Firebase';
import { useAuth } from '../../Context/AuthContext';
import './CustomSite.css';
import CustomSiteSchedule from './CustomSiteSchedule';
import Hero from './Hero';
import Navbar from './Navbar';
import Countdown from './Countdown';
import Details from './Details';
import OnTheDay from './OnTheDay';
import Gallery from './Gallery';
import FAQ from './FAQ';
import Gifts from './Gifts';
import Footer from './Footer';
import diamondIcon from '../../Assets/Icons/diamond.png';

const CustomSite = () => {
    const { friendlyURL } = useParams();
    const [searchParams] = useSearchParams();
    const { currentUser } = useAuth();

    const [weddingDate, setWeddingDate] = useState("");
    const [clientName, setClientName] = useState("");
    const [spouse, setSpouse] = useState("");
    const [loading, setLoading] = useState(true);
    const [hasPermission, setHasPermission] = useState(false);
    const [plan, setPlan] = useState('free');
    const [scheduleOpen, setScheduleOpen] = useState(false);
    const [schedule, setSchedule] = useState(null);
    const [daysUntilWedding, setDaysUntilWedding] = useState(0);
    const [weddingImg, setWeddingImg] = useState("");
    const [colourScheme, setColourScheme] = useState("pink");
    const [weddingTown, setWeddingTown] = useState("");
    const [weddingCounty, setWeddingCounty] = useState("");
    const [faqs, setFaqs] = useState([]);
    const [weddingVenueName, setWeddingVenueName] = useState("");
    const [weddingVenueAddress, setWeddingVenueAddress] = useState(null);
    const [weddingStartTime, setWeddingStartTime] = useState("");
    const [receptionVenueName, setReceptionVenueName] = useState("");
    const [receptionVenueAddress, setReceptionVenueAddress] = useState(null);
    const [receptionStartTime, setReceptionStartTime] = useState("");
    const [sharedVenue, setSharedVenue] = useState(false);
    const [weddingVenueTravelInfo, setWeddingVenueTravelInfo] = useState("");
    const [weddingReceptionTravelInfo, setWeddingReceptionTravelInfo] = useState("");
    const [otherImages, setOtherImages] = useState([]);
    const [meals, setMeals] = useState([]);
    const [whatToExpect, setWhatToExpect] = useState("");
    const [barInfo, setBarInfo] = useState("");
    const [dressCode, setDressCode] = useState("");
    const [accommodationOptions, setAccommodationOptions] = useState([]);
    const [hashtag, setHashtag] = useState("");
    const [giftRegistry, setGiftRegistry] = useState(null);
    const [showLoginModal, setShowLoginModal] = useState(false);
    const [loginEmail, setLoginEmail] = useState('');
    const [loginPassword, setLoginPassword] = useState('');
    const [loginError, setLoginError] = useState('');
    const [loginLoading, setLoginLoading] = useState(false);

    useEffect(() => {
        checkPermissionAndLoadWedding();
    }, [friendlyURL]);

    // Prepopulate email from query parameter when modal is shown for free users
    useEffect(() => {
        if (showLoginModal && !hasPermission) {
            const emailFromQuery = searchParams.get('email');
            if (emailFromQuery) {
                setLoginEmail(emailFromQuery);
            }
        }
    }, [showLoginModal, hasPermission, searchParams]);

    // Re-check permission when user logs in (token will be sent automatically via axios interceptor)
    useEffect(() => {
        if (currentUser && !loading) {
            // User logged in or auth state changed, re-check permission with token
            checkPermissionAndLoadWedding();
            setLoginLoading(false);
        }
    }, [currentUser]);

    useEffect(() => {
        if (!weddingDate) return;

        const calculateDays = () => {
            const now = new Date();
            const wedding = new Date(weddingDate);

            // Set both dates to midnight for accurate day calculation
            now.setHours(0, 0, 0, 0);
            wedding.setHours(0, 0, 0, 0);

            const difference = wedding.getTime() - now.getTime();
            const days = Math.ceil(difference / (1000 * 60 * 60 * 24));

            setDaysUntilWedding(days > 0 ? days : 0);
        };

        calculateDays();
    }, [weddingDate]);

    const checkPermissionAndLoadWedding = () => {
        setLoading(true);

        // Get auth token if user is logged in
        const getToken = async () => {
            if (currentUser) {
                try {
                    return await currentUser.getIdToken();
                } catch (error) {
                    console.error('Error getting token:', error);
                    return null;
                }
            }
            return null;
        };

        getToken().then(token => {
            const headers = {};
            if (token) {
                headers['AuthToken'] = token;
            }

            return axios.post(
                `${process.env.REACT_APP_API}/weddings/check-custom-site-permission`,
                { friendlyURL },
                { headers }
            );
        }).then(response => {
            // Always load wedding data for preview, even without permission
            if (response.data.wedding) {
                const wedding = response.data.wedding;
                setWeddingDate(wedding.weddingDate);
                setClientName(wedding.clientName);
                setSpouse(wedding.spouseName);
                setWeddingImg(wedding.img || "");
                setOtherImages(wedding.otherImages || []);
                setSchedule({ events: wedding.events || [] });
                setColourScheme(wedding.colourScheme || "pink");

                // Set venue details
                const ceremonyVenueName = wedding.weddingVenue ||
                    wedding.weddingVenueName ||
                    wedding.venueName ||
                    wedding.venue?.name ||
                    wedding.weddingVenueAddress?.name ||
                    "";
                setWeddingVenueName(ceremonyVenueName);
                setWeddingVenueAddress(wedding.weddingVenueAddress || null);
                setWeddingStartTime(
                    wedding.weddingStartTime ||
                    ""
                );

                // Check if shared venue - if true, use ceremony venue info for reception
                const isSharedVenue = wedding.sharedVenue === true;
                setSharedVenue(isSharedVenue);

                if (isSharedVenue) {
                    // Copy ceremony venue info to reception
                    setReceptionVenueName(ceremonyVenueName);
                    setReceptionVenueAddress(wedding.weddingVenueAddress || null);
                    setWeddingReceptionTravelInfo(wedding.weddingVenueTravelInfo || "");
                } else {
                    // Use separate reception venue info
                    setReceptionVenueName(
                        wedding.weddingReception ||
                        wedding.receptionName ||
                        wedding.weddingReceptionAddress?.name ||
                        ""
                    );
                    setReceptionVenueAddress(wedding.weddingReceptionAddress || null);
                    setWeddingReceptionTravelInfo(wedding.weddingReceptionTravelInfo || "");
                }

                setReceptionStartTime(wedding.receptionStartTime || "");
                setWeddingVenueTravelInfo(wedding.weddingVenueTravelInfo || "");

                // Set wedding town and county from venue address
                setWeddingTown(wedding.weddingVenueAddress?.town || "");
                setWeddingCounty(wedding.weddingVenueAddress?.county || "");

                // Set FAQs
                setFaqs(wedding.weddingFAQs || []);

                // Set meals
                setMeals(wedding.meals || []);

                // Set On The Day information
                setWhatToExpect(wedding.whatToExpect || "");
                setBarInfo(wedding.barInfo || "");
                setDressCode(wedding.dressCode || "");

                // Set accommodation options
                setAccommodationOptions(wedding.accommodationOptions || []);

                // Set hashtag
                setHashtag(wedding.hashtag || "");

                // Set gift registry
                setGiftRegistry(wedding.giftRegistry || null);
            }

            // Set permission status
            if (response.data.hasPermission) {
                setHasPermission(true);
                setShowLoginModal(false);
                // Clear login form on successful permission grant
                setLoginEmail('');
                setLoginPassword('');
                setLoginError('');
            } else {
                setHasPermission(false);
                setShowLoginModal(true);
            }
            // Set plan from response
            setPlan(response.data.plan || 'free');
            setLoading(false);
            setLoginLoading(false); // Reset login loading state
        })
            .catch(error => {
                console.error('Error checking permission:', error);
                setLoading(false);
                setLoginLoading(false);
                setShowLoginModal(true);
            });
    };

    if (loading) {
        return (
            <div className="container">
                <div className="loaderContainer">
                    <div className="loader"></div>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className={`custom-site-wrapper ${showLoginModal ? 'blurred' : ''}`}>
                <Navbar colourScheme={colourScheme} clientName={clientName} spouse={spouse} />

                <CustomSiteSchedule schedule={schedule} open={scheduleOpen} setOpen={setScheduleOpen} colourScheme={colourScheme} />

                {!scheduleOpen && schedule?.events?.filter(e => e.visible).length > 0 && (
                    <div id="schedule-tab" className={`${colourScheme}-schedule-tab`} onClick={() => setScheduleOpen(true)}>
                        <p>Schedule of the day</p>
                    </div>
                )}

                <div id="home">
                    <Hero
                        clientName={clientName}
                        spouse={spouse}
                        daysUntilWedding={daysUntilWedding}
                        weddingDate={weddingDate}
                        weddingImg={weddingImg}
                        colourScheme={colourScheme}
                        weddingTown={weddingTown}
                        weddingCounty={weddingCounty}
                        otherImages={otherImages}
                    />
                </div>

                <Countdown colourScheme={colourScheme} weddingDate={weddingDate} />

                <div id="details">
                    <Details
                        colourScheme={colourScheme}
                        weddingDate={weddingDate}
                        weddingStartTime={weddingStartTime}
                        weddingVenueName={weddingVenueName}
                        weddingVenueAddress={weddingVenueAddress}
                        weddingVenueTravelInfo={weddingVenueTravelInfo}
                        receptionVenueName={receptionVenueName}
                        receptionVenueAddress={receptionVenueAddress}
                        receptionStartTime={receptionStartTime}
                        weddingReceptionTravelInfo={weddingReceptionTravelInfo}
                        accommodationOptions={accommodationOptions}
                        sharedVenue={sharedVenue}
                    />
                </div>

                <div id="on-the-day">
                    <OnTheDay
                        colourScheme={colourScheme}
                        meals={meals}
                        whatToExpect={whatToExpect}
                        barInfo={barInfo}
                        dressCode={dressCode}
                    />
                </div>

                <div id="gallery">
                    <Gallery colourScheme={colourScheme} hashtag={hashtag} />
                </div>

                <div id="qna">
                    <FAQ colourScheme={colourScheme} faqs={faqs} />
                </div>

                <div id="gift-list">
                    <Gifts
                        colourScheme={colourScheme}
                        giftRegistryMessage={giftRegistry?.message || ""}
                        gifts={giftRegistry?.gifts || []}
                    />
                </div>

                <Footer colourScheme={colourScheme} />
            </div>

            {/* FAB - Upgrade to Diamond (only shown for free users) */}
            {plan === 'free' && (
                <div
                    className="upgrade-fab"
                    onClick={() => {
                        const userAgent = navigator.userAgent || window.opera;
                        const appDeepLink = 'firstdance://';
                        const androidStoreLink = 'https://play.google.com/store/apps/details?id=com.firstdance.firstdance';
                        const iosStoreLink = 'https://apps.apple.com/uk/app/first-dance/id6739738754';

                        // Try to open the app first
                        window.location.href = appDeepLink;

                        // Fallback to app store after delay if app doesn't open
                        setTimeout(() => {
                            if (/android/i.test(userAgent)) {
                                window.location.href = androidStoreLink;
                            } else if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
                                window.location.href = iosStoreLink;
                            }
                        }, 2500);
                    }}
                >
                    <img src={diamondIcon} alt="Diamond" className="upgrade-fab-icon" />
                    <span className="upgrade-fab-text">Upgrade to Share</span>
                </div>
            )}

            {showLoginModal && (
                <div
                    className="login-modal-overlay"
                    onClick={(e) => {
                        // Prevent closing modal when clicking overlay
                        e.stopPropagation();
                    }}
                >
                    <div
                        className="login-modal"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h2>Login to Preview</h2>
                        <p>You need to login to view this wedding website.</p>
                        <form
                            className="login-form"
                            onSubmit={async (e) => {
                                e.preventDefault();
                                setLoginLoading(true);
                                setLoginError('');

                                try {
                                    await signInWithEmailAndPassword(auth, loginEmail, loginPassword);
                                    // Login successful - AuthContext will update currentUser
                                    // The useEffect hook will trigger checkPermissionAndLoadWedding
                                    // which will close the modal if permission is granted
                                } catch (error) {
                                    console.error('Login error:', error);
                                    let errorMessage = 'Login failed. Please try again.';

                                    // Provide more specific error messages
                                    if (error.code === 'auth/user-not-found') {
                                        errorMessage = 'No account found with this email.';
                                    } else if (error.code === 'auth/wrong-password') {
                                        errorMessage = 'Incorrect password.';
                                    } else if (error.code === 'auth/invalid-email') {
                                        errorMessage = 'Invalid email address.';
                                    } else if (error.code === 'auth/user-disabled') {
                                        errorMessage = 'This account has been disabled.';
                                    } else if (error.code === 'auth/too-many-requests') {
                                        errorMessage = 'Too many failed attempts. Please try again later.';
                                    } else if (error.message) {
                                        errorMessage = error.message;
                                    }

                                    setLoginError(errorMessage);
                                    setLoginLoading(false);
                                }
                            }}
                        >
                            <div className="login-form-group">
                                <label htmlFor="login-email">Email</label>
                                <input
                                    id="login-email"
                                    type="email"
                                    value={loginEmail}
                                    onChange={(e) => setLoginEmail(e.target.value)}
                                    placeholder="Enter your email"
                                    required
                                    disabled={loginLoading}
                                />
                            </div>
                            <div className="login-form-group">
                                <label htmlFor="login-password">Password</label>
                                <input
                                    id="login-password"
                                    type="password"
                                    value={loginPassword}
                                    onChange={(e) => setLoginPassword(e.target.value)}
                                    placeholder="Enter your password"
                                    required
                                    disabled={loginLoading}
                                />
                            </div>
                            {loginError && (
                                <div className="login-error">
                                    {loginError}
                                </div>
                            )}
                            <button
                                type="submit"
                                className="login-modal-btn"
                                disabled={loginLoading}
                            >
                                {loginLoading ? 'Logging in...' : 'Login'}
                            </button>
                        </form>
                    </div>
                </div>
            )}
        </>
    );
};

export default CustomSite;
